import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';

type DeliveryChannel = 'app' | 'email';

interface NotificationControl {
  key: string;
  label: string;
  group: string;
  description: string;
  recipients: string;
  trigger: string;
  icon: string;
  iconClass: string;
  hasEmail: boolean;
  appKey: string;
  emailKey: string;
  appDefault: boolean;
  emailDefault: boolean;
}

@Component({
  selector: 'app-admin-notification-controls',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-full bg-[#F4F7FA] p-4 md:p-5">
      <div class="max-w-[1280px] mx-auto">
        <div class="flex flex-col xl:flex-row xl:items-end justify-between gap-3 mb-4">
          <div class="min-w-0">
            <p class="text-[9px] font-bold uppercase tracking-[0.16em] text-[#1A6FA6] mb-1">Delivery Rules</p>
            <h1 class="text-xl font-bold text-slate-950 tracking-tight">Notification Controls</h1>
            <p class="text-slate-500 text-[11px]">Manage delivery channels, workflow rules, manual review requests, and outgoing email copy.</p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <a routerLink="/admin/notifications" class="h-9 px-3 rounded-md bg-white border border-slate-200 text-slate-700 text-[11px] font-bold hover:bg-slate-50 flex items-center gap-2">
              <i class="ti ti-inbox text-[14px]"></i>
              Notification inbox
            </a>
            <button type="button" (click)="loadSettings()" [disabled]="settingsLoading()" class="h-9 px-3 rounded-md bg-white border border-slate-200 text-slate-700 text-[11px] font-bold hover:bg-slate-50 disabled:opacity-60 flex items-center gap-2">
              <i class="ti" [class.ti-refresh]="!settingsLoading()" [class.ti-loader]="settingsLoading()" [class.animate-spin]="settingsLoading()"></i>
              Refresh
            </button>
            <button type="button" (click)="saveSettings()" [disabled]="saving() || !settingsDirty()" class="h-9 px-3 rounded-md bg-[#0C3A5A] text-white text-[11px] font-bold hover:bg-[#082d46] disabled:opacity-45 flex items-center gap-2">
              <i class="ti" [class.ti-device-floppy]="!saving()" [class.ti-loader]="saving()" [class.animate-spin]="saving()"></i>
              Save controls
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div class="metric"><p>Master switches</p><strong>{{ masterControlsOn() }}/2</strong></div>
          <div class="metric"><p>App workflows</p><strong>{{ activeAppControls() }}/{{ notificationControls.length }}</strong></div>
          <div class="metric"><p>Email workflows</p><strong>{{ activeEmailControls() }}/{{ emailCapableControls() }}</strong></div>
          <div class="metric"><p>Review requests</p><strong class="text-amber-600">Manual</strong></div>
        </div>

        <div class="grid xl:grid-cols-[380px_minmax(0,1fr)] gap-4">
          <section class="space-y-4">
            <div class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
              <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                <div>
                  <p class="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Master Delivery</p>
                  <p class="text-xs text-slate-500 mt-0.5">Global gates for notification and email delivery.</p>
                </div>
                @if (settingsDirty()) {
                  <span class="h-6 px-2 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold flex items-center">Unsaved</span>
                }
              </div>
              <div class="p-4 space-y-3">
                <label class="toggle-card" [class.is-on]="settings().in_app_notifications_enabled">
                  <input type="checkbox" [ngModel]="settings().in_app_notifications_enabled" (ngModelChange)="updateSetting('in_app_notifications_enabled', $event)">
                  <span class="toggle-icon bg-sky-50 text-[#1A6FA6]"><i class="ti ti-bell-ringing"></i></span>
                  <span class="min-w-0 flex-1">
                    <strong>In-app notification inbox</strong>
                    <small>Creates persistent admin and mobile bell alerts for action-required work.</small>
                  </span>
                  <span class="switch"><span></span></span>
                </label>
                <label class="toggle-card" [class.is-on]="settings().email_notifications_enabled">
                  <input type="checkbox" [ngModel]="settings().email_notifications_enabled" (ngModelChange)="updateSetting('email_notifications_enabled', $event)">
                  <span class="toggle-icon bg-emerald-50 text-emerald-600"><i class="ti ti-mail"></i></span>
                  <span class="min-w-0 flex-1">
                    <strong>Email notifications</strong>
                    <small>Allows customer, supplier, staff, OTP, assignment, and document email delivery.</small>
                  </span>
                  <span class="switch"><span></span></span>
                </label>
              </div>
            </div>

            <div class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
              <div class="px-4 py-3 border-b border-slate-100">
                <p class="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Feedback And Email Copy</p>
              </div>
              <div class="p-4 space-y-3">
                <div class="rounded-lg border border-amber-100 bg-amber-50 p-3 flex items-start gap-3">
                  <span class="toggle-icon bg-white text-amber-600"><i class="ti ti-message-star"></i></span>
                  <span class="min-w-0 flex-1"><strong class="block text-xs text-amber-950">Manual after-job review request</strong><small class="block mt-1 text-[10px] text-amber-800">The assigned plumber sends it with one click after field work, or defers it to create plumber and admin reminders.</small></span>
                </div>
                <label class="block">
                  <span class="field-label">Global outgoing CC email</span>
                  <input type="email" [ngModel]="settings().outgoing_cc_email" (ngModelChange)="updateSetting('outgoing_cc_email', $event)" placeholder="accounts@example.com" class="field-input">
                  <span class="block mt-2 text-[10px] font-semibold text-slate-400">Copied on outgoing emails when email notifications are enabled. Skipped when it matches the main recipient.</span>
                </label>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-[#E7EEF5] overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div>
                <p class="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Workflow Matrix</p>
                <p class="text-xs text-slate-500 mt-0.5">Turn app and email delivery on or off per business workflow.</p>
              </div>
              <div class="flex flex-wrap gap-2">
                <button type="button" class="mini-btn" (click)="setAllChannel('app', true)">All app on</button>
                <button type="button" class="mini-btn" (click)="setAllChannel('app', false)">All app off</button>
                <button type="button" class="mini-btn" (click)="setAllChannel('email', true)">Email on</button>
                <button type="button" class="mini-btn" (click)="setAllChannel('email', false)">Email off</button>
              </div>
            </div>

            <div class="grid grid-cols-[minmax(0,1fr)_74px_74px] bg-slate-50 border-b border-slate-200">
              <div class="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Workflow</div>
              <div class="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">App</div>
              <div class="px-2 py-2 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Email</div>
            </div>
            @for (item of notificationControls; track item.key) {
              <div class="grid grid-cols-[minmax(0,1fr)_74px_74px] border-b border-slate-100 last:border-b-0 items-stretch">
                <div class="px-3 py-3 min-w-0">
                  <div class="flex items-start gap-2">
                    <span class="w-8 h-8 rounded-md flex items-center justify-center shrink-0" [ngClass]="item.iconClass">
                      <i class="ti text-[15px]" [class]="'ti-' + item.icon"></i>
                    </span>
                    <div class="min-w-0">
                      <div class="flex flex-wrap items-center gap-2">
                        <p class="text-[12px] font-bold text-slate-900 leading-tight">{{ item.label }}</p>
                        <span class="h-5 px-2 rounded-full bg-slate-100 text-slate-500 text-[9px] font-bold uppercase tracking-[0.08em]">{{ item.group }}</span>
                      </div>
                      <p class="text-[10px] text-slate-500 leading-snug mt-1">{{ item.description }}</p>
                      <div class="mt-2 grid md:grid-cols-2 gap-1 text-[10px] text-slate-400">
                        <span class="truncate"><i class="ti ti-bolt text-[12px]"></i> {{ item.trigger }}</span>
                        <span class="truncate"><i class="ti ti-users text-[12px]"></i> {{ item.recipients }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <label class="switch-cell" [class.opacity-40]="!settings().in_app_notifications_enabled" [title]="channelTitle(item, 'app')">
                  <input type="checkbox" [ngModel]="settings()[item.appKey]" (ngModelChange)="updateSetting(item.appKey, $event)" [disabled]="!settings().in_app_notifications_enabled">
                  <span></span>
                </label>
                <label class="switch-cell" [class.opacity-40]="!settings().email_notifications_enabled || !item.hasEmail" [title]="channelTitle(item, 'email')">
                  <input type="checkbox" [ngModel]="settings()[item.emailKey]" (ngModelChange)="updateSetting(item.emailKey, $event)" [disabled]="!settings().email_notifications_enabled || !item.hasEmail">
                  <span></span>
                </label>
              </div>
            }
          </section>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display:block; }
    .metric { background:white; border:1px solid #E7EEF5; border-radius:8px; padding:12px 14px; min-height:70px; }
    .metric p { font-size:10px; color:#64748b; font-weight:800; text-transform:uppercase; letter-spacing:.08em; margin:0 0 5px; }
    .metric strong { font-size:21px; line-height:1; color:#0f172a; }
    .mini-btn { height:34px; padding:0 13px; border:1px solid #dbe5ee; border-radius:7px; background:#f8fafc; color:#334155; font-size:11px; font-weight:800; }
    .mini-btn:hover { background:#eef6fb; border-color:#c7dfef; color:#0C3A5A; }
    .toggle-card { display:flex; align-items:center; gap:12px; padding:14px; border:1px solid #dbe5ee; border-radius:8px; cursor:pointer; background:white; transition:.16s ease; }
    .toggle-card.compact { padding:12px; }
    .toggle-card:hover, .toggle-card.is-on { border-color:#b8d8ea; background:#fbfdff; }
    .toggle-card input { position:absolute; opacity:0; pointer-events:none; }
    .toggle-icon { width:44px; height:44px; border-radius:10px; display:flex; align-items:center; justify-content:center; flex:none; font-size:18px; }
    .toggle-card.compact .toggle-icon { width:36px; height:36px; font-size:16px; }
    .toggle-card strong { display:block; font-size:12px; color:#0f172a; line-height:1.2; }
    .toggle-card small { display:block; font-size:10px; color:#52657d; line-height:1.35; margin-top:4px; }
    .switch { width:38px; height:22px; border-radius:999px; background:#cbd5e1; padding:2px; transition:.18s ease; flex:none; }
    .switch span { display:block; width:18px; height:18px; border-radius:999px; background:white; box-shadow:0 1px 2px rgba(15,23,42,.18); transition:.18s ease; }
    .toggle-card input:checked ~ .switch { background:#1A6FA6; }
    .toggle-card input:checked ~ .switch span { transform:translateX(16px); }
    .switch-cell { display:flex; align-items:center; justify-content:center; min-height:72px; cursor:pointer; }
    .switch-cell input { position:absolute; opacity:0; pointer-events:none; }
    .switch-cell span { width:36px; height:20px; border-radius:999px; background:#cbd5e1; position:relative; transition:.18s ease; }
    .switch-cell span::after { content:''; position:absolute; width:16px; height:16px; top:2px; left:2px; border-radius:999px; background:white; box-shadow:0 1px 2px rgba(15,23,42,.18); transition:.18s ease; }
    .switch-cell input:checked + span { background:#1A6FA6; }
    .switch-cell input:checked + span::after { transform:translateX(16px); }
    .switch-cell input:disabled + span { background:#e2e8f0; }
    .field-label { display:block; font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:.08em; color:#64748b; margin-bottom:6px; }
    .field-input { width:100%; height:38px; border:1px solid #dbe5ee; border-radius:7px; padding:0 11px; font-size:12px; color:#0f172a; outline:none; background:#f8fafc; }
    .field-input:focus { background:white; border-color:#1A6FA6; box-shadow:0 0 0 3px rgba(26,111,166,.1); }
  `]
})
export class AdminNotificationControlsComponent implements OnInit {
  private api = inject(ApiService);

  settingsLoading = signal(false);
  saving = signal(false);
  initialSettings = signal<any | null>(null);
  settings = signal<any>(this.defaultSettings());

  notificationControls: NotificationControl[] = [
    { key: 'job_assignment', label: 'Job assignments', group: 'Dispatch', description: 'Worker alerts when a job is allocated or changed.', recipients: 'Assigned workers', trigger: 'Job allocation', icon: 'briefcase', iconClass: 'bg-sky-50 text-[#1A6FA6]', hasEmail: true, appKey: 'notify_job_assignment_app', emailKey: 'notify_job_assignment_email', appDefault: true, emailDefault: true },
    { key: 'project_assignment', label: 'Project work assignments', group: 'Projects', description: 'Worker alerts for project allocations and scheduled project work.', recipients: 'Assigned project workers', trigger: 'Project allocation', icon: 'building-skyscraper', iconClass: 'bg-violet-50 text-violet-600', hasEmail: true, appKey: 'notify_project_assignment_app', emailKey: 'notify_project_assignment_email', appDefault: true, emailDefault: true },
    { key: 'leave_requests', label: 'Leave requests', group: 'Workforce', description: 'Admin review alerts and employee decision updates.', recipients: 'Approvers and employee', trigger: 'Submit or decision', icon: 'calendar-time', iconClass: 'bg-amber-50 text-amber-600', hasEmail: true, appKey: 'notify_leave_requests_app', emailKey: 'notify_leave_requests_email', appDefault: true, emailDefault: true },
    { key: 'fleet_updates', label: 'Fleet updates', group: 'Fleet', description: 'Vehicle assignment and fleet responsibility alerts.', recipients: 'Assigned worker', trigger: 'Vehicle assignment', icon: 'truck', iconClass: 'bg-teal-50 text-teal-600', hasEmail: false, appKey: 'notify_fleet_updates_app', emailKey: 'notify_fleet_updates_email', appDefault: true, emailDefault: false },
    { key: 'project_phase_reviews', label: 'Project phase reviews', group: 'Projects', description: 'Needs-changes and office review alerts for project forms.', recipients: 'Workers and office', trigger: 'Phase review', icon: 'clipboard-check', iconClass: 'bg-indigo-50 text-indigo-600', hasEmail: true, appKey: 'notify_project_phase_reviews_app', emailKey: 'notify_project_phase_reviews_email', appDefault: true, emailDefault: true },
    { key: 'quote_responses', label: 'Quote responses', group: 'Financials', description: 'Admin alerts when customers accept or decline quotes.', recipients: 'Admin team', trigger: 'Customer response', icon: 'file-dollar', iconClass: 'bg-emerald-50 text-emerald-600', hasEmail: false, appKey: 'notify_quote_responses_app', emailKey: 'notify_quote_responses_email', appDefault: true, emailDefault: false },
    { key: 'customer_feedback', label: 'Customer feedback', group: 'Customers', description: 'Manual review-request emails, reminders, and submitted feedback alerts.', recipients: 'Customers, plumbers and admins', trigger: 'Plumber action or customer response', icon: 'message-star', iconClass: 'bg-rose-50 text-rose-600', hasEmail: true, appKey: 'notify_customer_feedback_app', emailKey: 'notify_customer_feedback_email', appDefault: true, emailDefault: true },
    { key: 'document_updates', label: 'Documents and AI', group: 'Documents', description: 'Document, declaration, insurance and as-built workflow alerts.', recipients: 'Office users', trigger: 'Document workflow', icon: 'folders', iconClass: 'bg-slate-100 text-slate-600', hasEmail: false, appKey: 'notify_document_updates_app', emailKey: 'notify_document_updates_email', appDefault: true, emailDefault: false },
    { key: 'safety_compliance', label: 'Safety and compliance', group: 'Compliance', description: 'Audit, visa and safety action alerts.', recipients: 'Office and managers', trigger: 'Safety or audit issue', icon: 'shield-check', iconClass: 'bg-orange-50 text-orange-600', hasEmail: false, appKey: 'notify_safety_compliance_app', emailKey: 'notify_safety_compliance_email', appDefault: true, emailDefault: false },
    { key: 'operations_alerts', label: 'Operations alerts', group: 'Operations', description: 'Long-waiting unassigned jobs and idle active-shift alerts.', recipients: 'Dispatch and workers', trigger: 'Operational exception', icon: 'alarm', iconClass: 'bg-red-50 text-red-600', hasEmail: false, appKey: 'notify_operations_alerts_app', emailKey: 'notify_operations_alerts_email', appDefault: true, emailDefault: false },
  ];

  masterControlsOn = computed(() => Number(!!this.settings().in_app_notifications_enabled) + Number(!!this.settings().email_notifications_enabled));
  activeAppControls = computed(() => this.notificationControls.filter(item => !!this.settings()[item.appKey]).length);
  activeEmailControls = computed(() => this.notificationControls.filter(item => item.hasEmail && !!this.settings()[item.emailKey]).length);
  emailCapableControls = computed(() => this.notificationControls.filter(item => item.hasEmail).length);
  settingsDirty = computed(() => {
    const initial = this.initialSettings();
    return !!initial && JSON.stringify(this.normalizedSettings(this.settings())) !== JSON.stringify(this.normalizedSettings(initial));
  });

  ngOnInit() {
    this.loadSettings();
  }

  loadSettings() {
    this.settingsLoading.set(true);
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        const next = this.settingsFromResponse(res?.success ? res.data || {} : {});
        this.settings.set(next);
        this.initialSettings.set({ ...next });
        this.settingsLoading.set(false);
      },
      error: () => {
        this.settingsLoading.set(false);
        showToast('error', 'Failed to load notification controls.');
      }
    });
  }

  updateSetting(key: string, value: any) {
    this.settings.update(settings => ({ ...settings, [key]: value }));
  }

  setAllChannel(channel: DeliveryChannel, enabled: boolean) {
    const masterKey = channel === 'app' ? 'in_app_notifications_enabled' : 'email_notifications_enabled';
    this.settings.update(settings => {
      const next = { ...settings, [masterKey]: enabled };
      this.notificationControls.forEach(item => {
        if (channel === 'app') next[item.appKey] = enabled;
        if (channel === 'email' && item.hasEmail) next[item.emailKey] = enabled;
      });
      return next;
    });
  }

  saveSettings() {
    const ccEmail = String(this.settings().outgoing_cc_email || '').trim();
    if (ccEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ccEmail)) {
      showToast('error', 'Enter a valid global CC email address.');
      return;
    }

    const cleaned = { ...this.settings(), outgoing_cc_email: ccEmail };
    this.settings.set(cleaned);
    this.saving.set(true);
    this.api.updateCompanySettings(this.normalizedSettings(cleaned)).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res.success) {
          this.initialSettings.set({ ...cleaned });
          showToast('success', 'Notification controls saved.');
        } else {
          showToast('error', res.message || 'Failed to save notification controls.');
        }
      },
      error: () => {
        this.saving.set(false);
        showToast('error', 'Network error while saving notification controls.');
      }
    });
  }

  channelTitle(item: NotificationControl, channel: DeliveryChannel): string {
    if (channel === 'email' && !item.hasEmail) return 'Email is not used by this workflow yet.';
    const masterOff = channel === 'app' ? !this.settings().in_app_notifications_enabled : !this.settings().email_notifications_enabled;
    if (masterOff) return `${channel === 'app' ? 'In-app' : 'Email'} delivery is disabled globally.`;
    return `${item.label} ${channel === 'app' ? 'in-app' : 'email'} delivery.`;
  }

  private settingsFromResponse(s: any): any {
    const next = this.defaultSettings();
    next.email_notifications_enabled = this.toBool(s.email_notifications_enabled, true);
    next.in_app_notifications_enabled = this.toBool(s.in_app_notifications_enabled, true);
    next.outgoing_cc_email = s.outgoing_cc_email || '';
    this.notificationControls.forEach(item => {
      next[item.appKey] = this.toBool(s[item.appKey], item.appDefault);
      next[item.emailKey] = this.toBool(s[item.emailKey], item.emailDefault);
    });
    return next;
  }

  private defaultSettings(): any {
    return {
      email_notifications_enabled: true,
      in_app_notifications_enabled: true,
      outgoing_cc_email: '',
      notify_job_assignment_app: true,
      notify_job_assignment_email: true,
      notify_project_assignment_app: true,
      notify_project_assignment_email: true,
      notify_leave_requests_app: true,
      notify_leave_requests_email: true,
      notify_fleet_updates_app: true,
      notify_fleet_updates_email: false,
      notify_project_phase_reviews_app: true,
      notify_project_phase_reviews_email: true,
      notify_quote_responses_app: true,
      notify_quote_responses_email: false,
      notify_customer_feedback_app: true,
      notify_customer_feedback_email: true,
      notify_document_updates_app: true,
      notify_document_updates_email: false,
      notify_safety_compliance_app: true,
      notify_safety_compliance_email: false,
      notify_operations_alerts_app: true,
      notify_operations_alerts_email: false
    };
  }

  private normalizedSettings(settings: any): any {
    const payload: any = {
      email_notifications_enabled: settings.email_notifications_enabled ? '1' : '0',
      in_app_notifications_enabled: settings.in_app_notifications_enabled ? '1' : '0',
      outgoing_cc_email: String(settings.outgoing_cc_email || '').trim()
    };
    this.notificationControls.forEach(item => {
      payload[item.appKey] = settings[item.appKey] ? '1' : '0';
      payload[item.emailKey] = settings[item.emailKey] ? '1' : '0';
    });
    return payload;
  }

  private toBool(value: any, fallback: boolean): boolean {
    if (value === undefined || value === null || value === '') return fallback;
    const normalized = String(value).trim().toLowerCase();
    if (['1', 'true', 'yes', 'on', 'enabled'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off', 'disabled'].includes(normalized)) return false;
    return fallback;
  }
}
